import { useCallback, useState } from 'react'

import { WebViewerInstance } from '@pdftron/webviewer'

import { v4 } from 'uuid'
import { FormStatus, ObservationStatus, TaskStatus, Trade } from '@types'
import { Coordinates, Marker, MarkerType } from '../type'
import { getCoordinatesFromEvent } from '../utils'

interface DragState {
  isDragging: boolean
}

type MarkerStatus = 'visible' | 'add_selecting_position' | 'add_dragging_position'

interface UseMarkerProps {
  instanceRef: React.MutableRefObject<WebViewerInstance | null>
  pageNumber: number
  initialMarker?: Marker
  markerColor?: string
  idNumber?: number
  status?: ObservationStatus | FormStatus | TaskStatus
  markerType: MarkerType
  onMarkerMoveEnd?: (coordinates: Coordinates) => void
  onDelete?: () => void
  onAdd?: (coordinates: Coordinates) => void
}

interface MarkerEventHandlers {
  handleMove: (event: MouseEvent | TouchEvent) => void
  handleEnd: (event: MouseEvent | TouchEvent) => void
  handleMarkerMouseDown: () => void
  addMarker: (coordinates: Coordinates) => void
  deleteMarker: () => void
  setCoordinates: (coordinates: Coordinates) => void
  setMarker: (marker: Marker) => void
}

interface UseMarkerReturn {
  // State
  marker?: Marker
  markerStatus: MarkerStatus
  dragState: DragState
  // Actions
  setMarkerStatus: (status: MarkerStatus) => void
  // Event Handlers
  eventHandlers: MarkerEventHandlers
}

export const useMarker = ({
  instanceRef,
  pageNumber,
  initialMarker,
  markerColor,
  idNumber,
  status,
  markerType,
  onMarkerMoveEnd,
  onDelete,
  onAdd,
}: UseMarkerProps): UseMarkerReturn => {
  const [marker, setMarker] = useState<Marker | undefined>(initialMarker)
  const [markerStatus, setMarkerStatus] = useState<MarkerStatus>('visible')
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
  })

  const handleMove = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!dragState.isDragging || !marker) return

      const coordinates = getCoordinatesFromEvent(event, instanceRef, pageNumber)
      if (!coordinates) return

      setMarker((current) => (current ? { ...current, ...coordinates } : undefined))
    },
    [dragState.isDragging, marker, instanceRef, pageNumber],
  )

  const handleEnd = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (dragState.isDragging && marker) {
        const coordinates = getCoordinatesFromEvent(event, instanceRef, pageNumber)
        if (coordinates) {
          onMarkerMoveEnd?.(coordinates)
        }
      }

      setDragState({ isDragging: false })
    },
    [dragState.isDragging, marker, instanceRef, onMarkerMoveEnd, pageNumber],
  )

  const handleMarkerMouseDown = useCallback(() => {
    setDragState({ isDragging: true })
  }, [])

  const addMarker = useCallback(
    (coordinates: Coordinates) => {
      const newMarker: Marker = {
        id: v4(),
        pageNumber,
        type: markerType,
        ...coordinates,
        data: {
          trade: {
            color: markerColor,
          } as Trade,
          idNumber,
          status,
        },
      }

      setMarker(newMarker)
      setMarkerStatus('visible')

      onAdd?.(coordinates)
    },
    [idNumber, markerColor, onAdd, pageNumber, status, markerType],
  )

  const setCoordinates = useCallback((coordinates: Coordinates) => {
    setMarker((current) => (current ? { ...current, ...coordinates } : undefined))
  }, [])

  const deleteMarker = useCallback(() => {
    onDelete?.()
    setMarker(undefined)
    setMarkerStatus('visible')
  }, [onDelete])

  const eventHandlers: MarkerEventHandlers = {
    handleMove,
    handleEnd,
    handleMarkerMouseDown,
    addMarker,
    deleteMarker,
    setCoordinates,
    setMarker,
  }

  return {
    // State
    marker,
    markerStatus,
    dragState,
    // Actions
    setMarkerStatus,
    // Event Handlers
    eventHandlers,
  }
}
